import { apiRequest } from '@rh/shared/lib/api-client'
import z from 'zod'
import { TrialProcessOptions as SharedTrialProcessOptions } from '@rh/shared/types/trial-options'
import type { SavedDesign } from '@rh/shared/schemas/design'
import {
  parseContentBoundsReport,
  type ContentBoundsReport,
} from '@rh/shared/utils/content-trim'

/**
 * Redaction options for PDF processing
 */
export interface RedactionOptions {
  phoneNumbers: boolean
  socialLinks: boolean
  emails: boolean
  urls: boolean
  name: string | null
  keywords: string[]
  caseSensitive: boolean
}

/**
 * Response from processing a resume PDF
 */
export interface ProcessResumeResponse {
  url: string
  expiresAt: string
  /**
   * Fonts the uploaded document requested that the converter had to
   * substitute — the PDF layout may differ from the original document.
   * Empty/absent for PDF uploads and fully-covered documents.
   */
  missingFonts?: string[]
}

/**
 * Process a resume and get the full response including expiration
 * @param resumeHash - The hash/key of the resume in S3
 * @param agencyId - The agency ID to use for branding and profile
 * @param redactionOptions - Optional redaction settings
 * @returns Full response with URL and expiration time
 */
async function processResumeWithDetails(
  resumeHash: string,
  agencyId: string,
  options?: {
    redactionOptions?: RedactionOptions
    writeup?: string
  }
): Promise<ProcessResumeResponse> {
  const response = await apiRequest<ProcessResumeResponse>('/resumes/process', {
    method: 'POST',
    json: {
      resumeHash,
      agencyId,
      redactionOptions: options?.redactionOptions,
      writeup: options?.writeup,
    },
  })

  return response
}

/**
 * Response from processing a trial resume
 */
export interface TrialProcessResumeResponse {
  url: string
  expiresAt: string
}

export const customizeOptionsSchema = z.object({
  // Global/Generic values
  textColor: z.string().max(200).nullish(),
  borderColor: z.string().max(200).nullish(),
  agencyBrandColor: z.string().max(200).nullish(),
  fontFamily: z.string().max(200).nullish(),
  writeup: z.string().nullish(),

  // Watermark
  includeWatermark: z.boolean().nullish(),
  watermarkText: z.string().max(200).nullish(),
  watermarkColor: z.string().max(200).nullish(),
  watermarkTransparency: z.number().min(0).max(100).nullish(),

  // Header configuration
  includeHeader: z.boolean().nullish(),
  headerShape: z
    .enum(['columns-1-image', 'columns-1-text', 'columns-2', 'columns-3'])
    .nullish(),

  // Header agency data
  headerAgencyLogo: z.string().nullish(),
  headerAgencyName: z.string().max(200).nullish(),
  headerAgencyAddressLine1: z.string().max(200).nullish(),
  headerAgencyAddressLine2: z.string().max(200).nullish(),
  headerAgencyAddressLine3: z.string().max(200).nullish(),
  headerAgencyPhoneNumber: z.string().max(200).nullish(),
  headerAgencyUrl: z.string().max(200).nullish(),
  headerAgencyEmail: z.string().max(200).nullish(),
  headerAgencyLinkedIn: z.string().max(200).nullish(),

  // Header agency visibility
  showHeaderAgencyLogo: z.boolean().nullish(),
  showHeaderAgencyName: z.boolean().nullish(),
  showHeaderAgencyAddressLine1: z.boolean().nullish(),
  showHeaderAgencyAddressLine2: z.boolean().nullish(),
  showHeaderAgencyAddressLine3: z.boolean().nullish(),
  showHeaderAgencyPhoneNumber: z.boolean().nullish(),
  showHeaderAgencyUrl: z.boolean().nullish(),
  showHeaderAgencyEmailAddress: z.boolean().nullish(),
  showHeaderAgencyLinkedIn: z.boolean().nullish(),

  // Header recruiter data
  headerRecruiterName: z.string().max(200).nullish(),
  headerRecruiterPhoneNumber: z.string().max(200).nullish(),
  headerRecruiterEmail: z.string().max(200).nullish(),
  headerRecruiterLinkedIn: z.string().max(200).nullish(),

  // Header recruiter visibility
  showHeaderRecruiterName: z.boolean().nullish(),
  showHeaderRecruiterPhoneNumber: z.boolean().nullish(),
  showHeaderRecruiterEmailAddress: z.boolean().nullish(),
  showHeaderRecruiterLinkedIn: z.boolean().nullish(),

  // Footer configuration
  includeFooter: z.boolean().nullish(),
  footerShape: z
    .enum(['columns-1-image', 'columns-1-text', 'columns-2', 'columns-3'])
    .nullish(),

  // Footer agency data
  footerAgencyLogo: z.string().nullish(),
  footerAgencyName: z.string().max(200).nullish(),
  footerAgencyAddressLine1: z.string().max(200).nullish(),
  footerAgencyAddressLine2: z.string().max(200).nullish(),
  footerAgencyAddressLine3: z.string().max(200).nullish(),
  footerAgencyPhoneNumber: z.string().max(200).nullish(),
  footerAgencyUrl: z.string().max(200).nullish(),
  footerAgencyEmail: z.string().max(200).nullish(),
  footerAgencyLinkedIn: z.string().max(200).nullish(),

  // Footer agency visibility
  showFooterAgencyLogo: z.boolean().nullish(),
  showFooterAgencyName: z.boolean().nullish(),
  showFooterAgencyDetails: z.boolean().nullish(),
  showFooterAgencyAddressLine1: z.boolean().nullish(),
  showFooterAgencyAddressLine2: z.boolean().nullish(),
  showFooterAgencyAddressLine3: z.boolean().nullish(),
  showFooterAgencyPhoneNumber: z.boolean().nullish(),
  showFooterAgencyUrl: z.boolean().nullish(),
  showFooterAgencyEmailAddress: z.boolean().nullish(),
  showFooterAgencyLinkedIn: z.boolean().nullish(),

  // Footer recruiter data
  footerRecruiterName: z.string().max(200).nullish(),
  footerRecruiterPhoneNumber: z.string().max(200).nullish(),
  footerRecruiterEmail: z.string().max(200).nullish(),
  footerRecruiterLinkedIn: z.string().max(200).nullish(),

  // Footer recruiter visibility
  showFooterRecruiterName: z.boolean().nullish(),
  showFooterRecruiterPhoneNumber: z.boolean().nullish(),
  showFooterRecruiterEmailAddress: z.boolean().nullish(),
  showFooterRecruiterLinkedIn: z.boolean().nullish(),
})

export type TrialProcessOptions = SharedTrialProcessOptions

/**
 * Process a resume in trial mode (no authentication required)
 * @param resumeHash - The hash/key of the resume in S3
 * @param headerText - Optional custom header text
 * @returns Full response with URL and expiration time
 */
async function processTrialResume(
  resumeHash: string,
  options?: TrialProcessOptions
): Promise<TrialProcessResumeResponse> {
  const response = await apiRequest<TrialProcessResumeResponse>(
    '/resumes/trial/process',
    {
      method: 'POST',
      json: {
        resumeHash,
        options,
      },
    }
  )

  return response
}

/**
 * Fetch the server-measured content margins for an uploaded document (used
 * by the trial live preview to mirror the composer's margin trim). Null on
 * any failure — the preview then renders untrimmed, exactly matching a
 * composer that couldn't resolve bounds either.
 */
async function fetchContentBounds(
  resumeHash: string
): Promise<ContentBoundsReport | null> {
  try {
    const response = await apiRequest<{ bounds: unknown }>(
      `/resumes/content-bounds/${encodeURIComponent(resumeHash)}`,
      { method: 'GET' }
    )
    return parseContentBoundsReport(response.bounds)
  } catch {
    return null
  }
}

/**
 * Fetch the default resume PDF from the public API endpoint.
 */
async function fetchDefaultResumePdf(): Promise<File> {
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
  const response = await fetch(`${apiBaseUrl}/sample-resume`, {
    method: 'GET',
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch default resume: ${response.status} ${response.statusText}`
    )
  }

  const blob = await response.blob()
  return new File([blob], 'default-resume.pdf', {
    type: 'application/pdf',
    lastModified: Date.now(),
  })
}

export interface TrialDefaultDesign {
  source: 'uploaded' | 'built-in'
  design: SavedDesign
  /**
   * The color the design was authored in (the placeholder declared at
   * upload, or #52547a). Used as the brand color until the user picks one.
   */
  defaultBrandColor: string
  /** Image data URLs keyed by the design's assetKeys. */
  images: Record<string, string>
}

let trialDefaultDesignPromise: Promise<TrialDefaultDesign | null> | null = null

/**
 * The base design the trial page should preview: the admin-uploaded
 * default when one exists, else the checked-in DEFAULT_DESIGN (which the
 * server also returns as the fallback). Null on failure — callers keep the
 * compiled DEFAULT_DESIGN, which is the server's fallback too, so preview
 * and download can only disagree while the request is actually broken.
 * Fetched once per session (public endpoint, rarely changes).
 */
function fetchTrialDefaultDesign(): Promise<TrialDefaultDesign | null> {
  trialDefaultDesignPromise ??= apiRequest<TrialDefaultDesign>(
    '/resumes/trial/default-design'
  ).catch(() => {
    trialDefaultDesignPromise = null
    return null
  })
  return trialDefaultDesignPromise
}

/**
 * Brand color for a user who never picked one: the uploaded default
 * design's authored theme color, so the seeded agency looks as the admin
 * intended. Without an uploaded default (or on fetch failure) it stays
 * the historical '#000000' fallback — behavior unchanged.
 */
async function defaultOnboardingBrandColor(): Promise<string> {
  const result = await fetchTrialDefaultDesign()
  return result?.source === 'uploaded' ? result.defaultBrandColor : '#000000'
}

/**
 * Report a processed-resume download so the server writes a
 * `resume.download` audit entry — the app's core usage signal. Fire and
 * forget: reporting must never affect the user's download.
 */
function trackResumeDownload(
  resumeHash: string,
  agencyId: string,
  filename?: string
): void {
  void apiRequest('/resumes/download-event', {
    method: 'POST',
    json: { resumeHash, agencyId, filename },
  }).catch(() => {
    // Swallow — an unrecorded download beats a broken download button.
  })
}

export const PdfService = {
  processResumeWithDetails,
  processTrialResume,
  fetchContentBounds,
  fetchDefaultResumePdf,
  fetchTrialDefaultDesign,
  defaultOnboardingBrandColor,
  trackResumeDownload,
}
